import { FC, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateConversationPhrase } from '@/entities/conversations';
import { buildCreateRequest, buildNewRecordFormValues } from './recordForm';
import { useTranscriptEditorContext } from './TranscriptEditorContext';
import { TranscriptRecordForm } from './TranscriptRecordForm';
import { TranscriptRecordFormFooter } from './TranscriptRecordFormFooter';
import { TranscriptRecordFormFrame } from './TranscriptRecordFormFrame';
import type { TranscriptEditorForm } from './types';
import { useTranscriptRecordForm } from './useTranscriptRecordForm';

type Props = {
  form: Extract<TranscriptEditorForm, { mode: 'create' }>;
};

/**
 * Новая запись выбранного вида на месте промежутка между записями: границы заполнены границами промежутка.
 */
export const TranscriptNewRecordForm: FC<Props> = ({ form: openedForm }) => {
  const { t } = useTranslation(['dialogs']);
  const { conversationId, closeForm, reportDirty } = useTranscriptEditorContext();
  const { mutate: createPhrase, isPending } = useCreateConversationPhrase();
  const { type } = openedForm;
  const form = useTranscriptRecordForm(type, buildNewRecordFormValues(openedForm));
  const personId = useWatch({ control: form.control, name: 'personId' });

  const { isDirty } = form.formState;
  useEffect(() => {
    reportDirty(isDirty);
  }, [isDirty, reportDirty]);

  const handleSave = form.handleSubmit((values) => {
    createPhrase({ conversationId, data: buildCreateRequest(type, values) }, { onSuccess: closeForm });
  });

  return (
    <TranscriptRecordFormFrame type={type} personId={personId}>
      <TranscriptRecordForm
        form={form}
        type={type}
        title={t(`dialogs:transcriptEdit.createTitle.${type}`)}
        onSubmit={() => void handleSave()}
      >
        <TranscriptRecordFormFooter
          isDirty={isDirty}
          isSaving={isPending}
          isNew
          onSave={() => void handleSave()}
          onCancel={closeForm}
        />
      </TranscriptRecordForm>
    </TranscriptRecordFormFrame>
  );
};
